import type { QueryClient } from "@tanstack/react-query";
import {
	createRootRouteWithContext,
	type ErrorComponentProps,
	Outlet,
	useLocation,
	useMatch,
	useNavigate,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorFallback";
import { NotFound } from "@/components/NotFound/NotFound";
import { useAuth } from "@/hooks/useAuth";
import type { MenuItem } from "@/models";
import { getEnvironment } from "@/services/queries/settings";
import { initSentry } from "@/utils/sentry";
import { BottomNav } from "./-components/BottomNav";
import { Sidebar } from "./-components/Sidebar";

interface RouterContext {
	queryClient: QueryClient;
	routeMetadata?: MenuItem;
}

// Wrapper to adapt ErrorFallback for TanStack Router
function RouterErrorComponent({ error, reset }: Readonly<ErrorComponentProps>) {
	return <ErrorFallback error={error} resetErrorBoundary={reset} />;
}

export const Route = createRootRouteWithContext<RouterContext>()({
	component: RootComponent,
	errorComponent: RouterErrorComponent,
	notFoundComponent: NotFound,
});

function RootComponent() {
	const isBare = useMatch({ from: "/_bare", shouldThrow: false });
	const navigate = useNavigate();
	const location = useLocation();
	const { queryClient } = Route.useRouteContext();
	const { isAuthenticated, authStatus, fetchAuthStatus } = useAuth();
	const [authChecked, setAuthChecked] = useState(false);

	useEffect(() => {
		fetchAuthStatus()
			.then(() => setAuthChecked(true))
			.catch(() => setAuthChecked(true));
	}, [fetchAuthStatus]);

	useEffect(() => {
		if (!authChecked || !authStatus) return;

		if (authStatus.authEnabled && !isAuthenticated && !isBare && location.pathname !== "/login") {
			const currentPath = location.pathname + location.searchStr;
			navigate({ to: "/login", search: { redirect: currentPath } });
		}
	}, [authChecked, authStatus, isAuthenticated, isBare, navigate, location]);

	useEffect(() => {
		if (!authChecked) return;
		if (!isAuthenticated && authStatus?.authEnabled) return;

		queryClient
			.fetchQuery(getEnvironment())
			.then((env) => {
				if (env.enableStatistics) {
					initSentry();
				}
			})
			.catch(() => {});
	}, [authChecked, isAuthenticated, authStatus, queryClient]);

	if (!authChecked) {
		return (
			<div className="flex items-center justify-center h-screen bg-background">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
			</div>
		);
	}

	if (isBare) {
		return (
			<ErrorBoundary FallbackComponent={ErrorFallback}>
				<Outlet />
			</ErrorBoundary>
		);
	}

	return (
		<div className="flex w-full text-white bg-background md:h-dvh md:overflow-hidden">
			<Sidebar />

			<main className="flex-1 pb-20 md:pb-0 min-h-dvh md:min-h-0 md:h-dvh md:overflow-auto bg-background">
				<ErrorBoundary FallbackComponent={ErrorFallback}>
					<Outlet />
				</ErrorBoundary>
			</main>

			<BottomNav />
		</div>
	);
}
